/**
 * Tenant configuration — read from Vite env vars at build time.
 *
 * Every tenant deploy sets its own VITE_TENANT_* vars in Netlify env config
 * so the same frontend bundle can be rebuilt per tenant with distinct branding.
 * Matches the backend `app/tenant.py` shape.
 */

interface TenantConfig {
  name: string
  primaryColor: string
  accentColor: string
  apiUrl: string
  /** Fallback single-letter mark shown in a coloured square if logoImageUrl is empty. */
  logoMonogram: string
  /** Path to a real logo image (e.g. /greystone-logo.png). When set, overrides the monogram. */
  logoImageUrl: string
  /**
   * Absolute public origin for canonical URLs + Open Graph (e.g. https://greystone.loanloop.com.au).
   * At go-live this becomes the tenant's own domain and the bundle is rebuilt once.
   * No trailing slash.
   */
  siteUrl: string
  /**
   * Marketing size of the lender panel ("we compare N+ lenders"). Single source of
   * truth for the count shown across the site (LenderWall headline + SEO copy) so it
   * can never drift from the /services copy. Used as a floor — if the live panel ever
   * exceeds it, the larger real number wins. Greystone: 60 (LMG aggregator access).
   */
  lenderCount: number
  /**
   * Property-report tool (Cotality). OFF by default — the tool is only useful
   * with PRODUCTION Cotality credentials (sandbox returns ~24 test properties
   * only, which would break on a live customer address). Set
   * VITE_PROPERTY_REPORT_ENABLED=true once production creds are wired.
   */
  propertyReportEnabled: boolean
}

const DEFAULT_NAME = 'Greystone Finance'
const DEFAULT_PRIMARY = '#0A0A0A'
const DEFAULT_ACCENT = '#E2FF60'
// Empty string → all fetches go to the current origin, and Vite dev-server's
// /api proxy (vite.config.ts) forwards them to the backend at :8000.
// Prod tenant deploys set VITE_API_GATEWAY_URL to the absolute API host.
const DEFAULT_API = ''
const DEFAULT_MONOGRAM = 'G'
const DEFAULT_LOGO_IMAGE = '/greystone-logo.png'
const DEFAULT_SITE_URL = 'https://greystone.loanloop.com.au'
const DEFAULT_LENDER_COUNT = 60

// Strip any accidental trailing slash so `${siteUrl}${path}` never double-slashes.
const rawSiteUrl = import.meta.env.VITE_TENANT_SITE_URL || DEFAULT_SITE_URL

export const tenantConfig: TenantConfig = {
  name: import.meta.env.VITE_TENANT_NAME || DEFAULT_NAME,
  primaryColor: import.meta.env.VITE_TENANT_PRIMARY_COLOR || DEFAULT_PRIMARY,
  accentColor: import.meta.env.VITE_TENANT_ACCENT_COLOR || DEFAULT_ACCENT,
  apiUrl: import.meta.env.VITE_API_GATEWAY_URL || DEFAULT_API,
  logoMonogram: import.meta.env.VITE_TENANT_LOGO_MONOGRAM || DEFAULT_MONOGRAM,
  logoImageUrl: import.meta.env.VITE_TENANT_LOGO_URL || DEFAULT_LOGO_IMAGE,
  siteUrl: rawSiteUrl.replace(/\/+$/, ''),
  lenderCount: Number(import.meta.env.VITE_TENANT_LENDER_COUNT) || DEFAULT_LENDER_COUNT,
  propertyReportEnabled: import.meta.env.VITE_PROPERTY_REPORT_ENABLED === 'true',
}
